import { Accordion, AccordionDetails, Box, Grid, Icon, Stack, Tooltip } from '@mui/material'
import { AccordionTitle } from 'components/styled/Accordions'
import { getEventTypeInfo } from 'events/utils/EventUtils'
import { countBy, groupBy, isUndefined, keys, orderBy, truncate, uniq } from 'lodash'
import React, { useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { nbPerPageLabelShort } from 'referencial/constants/ReferencialConstants'
import { getDate, getYear } from 'utils/DateUtil'
import useListIndexed from 'utils/customHook/useListIndexed'
import useSandreList from 'utils/customHook/useSandreList'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import DtoInterventionEvent from 'piezometry/dto/DtoInterventionEvent'
import { NewTable } from 'components/datatable/NewTable'

const GroupAccordion = ({
    defaultExpanded = false,
    title = i18n.unknown,
    events = [],
    headers = [],
}) => {
    const allInterventionType = events.flatMap(e => e.interventions?.map(i => i.split(':')[0]) ?? [])
    const {
        CURATIF: nbCurative = 0,
        PREVENTIF: nbPreventive = 0,
        AUTRE: nbOther = 0,
    } = countBy(allInterventionType, i => i)

    return (
        <Accordion defaultExpanded={defaultExpanded}>
            <AccordionTitle title={(
                <Grid container justifyContent='space-between'>
                    { title.length > 40 ? (<Tooltip title={title}>
                        {truncate(title, {
                            length: 40,
                            separator: ' ',
                        }) }
                    </Tooltip>
                    ) : title }
                    <Stack direction='row'
                        spacing={7}
                        divider={<Box/>}
                    >
                        {`${nbCurative} Curatifs`}
                        {`${nbPreventive} Préventifs`}
                        {`${nbOther} Autres`}
                    </Stack>
                </Grid>
            )}
            />
            <AccordionDetails sx={{ padding: 0 }}>
                <NewTable
                    rows={orderBy(events, ['stationCode', 'timestamp'], ['asc', 'desc'])}
                    rowsPerPageOptions={nbPerPageLabelShort}
                    headers={headers}
                    id={title}
                />
            </AccordionDetails>
        </Accordion>
    )
}

GroupAccordion.propTypes = {
    defaultExpanded: PropTypes.bool,
    title: PropTypes.string,
    events: PropTypes.arrayOf(PropTypes.shape({
        stationCode: PropTypes.string,
        timestamp: PropTypes.number,
        interventions: PropTypes.arrayOf(PropTypes.string),
    })),
    headers: PropTypes.arrayOf(PropTypes.string),
}

const TooltipList = ({
    listLabel = [],
}) => (
    <div>
        {listLabel.map(label => (
            <Grid key={label}>
                <span>- {label}</span>
                <br />
            </Grid>
        ))}
    </div>
)

TooltipList.propTypes = {
    listLabel: PropTypes.arrayOf(PropTypes.string),
}

const EventsGroupByDate = ({
    events = [],
}) => {
    const {
        eventDiagnostics,
        eventSolutions,
        piezometers,
    } = useSelector(store => ({
        eventDiagnostics: store.EventsReducer.eventPiezometerDiagnostics,
        eventSolutions: store.EventsReducer.eventPiezometerSolutions,
        piezometers: store.PiezometryReducer.piezometersLight,
    }), shallowEqual)

    const piezometersIndexed = useListIndexed(piezometers)

    const interventionPreventive = useSandreList('CAUSE_INTERVENTION.PREVENTIF')
    const interventionCurative = useSandreList('CAUSE_INTERVENTION.CURATIF')
    const interventionOther = useSandreList('CAUSE_INTERVENTION.AUTRE')

    const interventions = {
        PREVENTIF: interventionPreventive,
        CURATIF: interventionCurative,
        AUTRE: interventionOther,
    }

    const eventTypeInfo = useMemo(() => getEventTypeInfo(), [])

    const formattedEvents = events.map(e => {
        const causes = e.interventions?.map(intervention => {
            const [type, id] = intervention.split(':')
            const parseId = parseInt(id)
            return interventions[type]?.find(i => i.code === parseId)?.name ?? ''
        })

        const diagnostics = e.uniqDiagnostics.map(id => eventDiagnostics.find(d => d.codeDiagnostic === id)?.description).filter(d => !!d)
        const solutions = e.uniqSolutions.map(id => eventSolutions.find(d => d.codeSolution === id)?.description).filter(d => !!d)

        const piezo = piezometersIndexed[e.piezometer]
        return {
            eventType: {
                value: (
                    <Grid container spacing={1}>
                        <Icon color='black'>{eventTypeInfo[e.eventType].icon}</Icon>
                        {eventTypeInfo[e.eventType].label}
                    </Grid>
                ),
                sortValue: eventTypeInfo[e.eventType].label,
            },
            date: { value: getDate(e.date), sortValue: e.date },
            station: {
                value: (
                    <Tooltip title={piezo?.name}>
                        {piezo?.code}
                    </Tooltip>
                ),
                sortValue: piezo?.code,
            },
            cause: { value: causes?.join(', ') ?? '' },
            diagnostics: {
                value: e.uniqDiagnostics.length ? (
                    <Tooltip title={(<TooltipList listLabel={diagnostics} />)}>
                        {e.uniqDiagnostics.length}
                    </Tooltip>
                ) : 0,
                sortValue: e.uniqDiagnostics.length,
            },
            actions: { value: e.uniqSolutions.length ? (
                <Tooltip title={(<TooltipList listLabel={solutions} />)}>
                    {e.uniqSolutions.length}
                </Tooltip>
            ) : 0,
            sortValue: e.uniqSolutions.length,
            },
            timestamp: e.date,
            stationCode: piezo?.code,
            interventions: e.interventions,
        }
    })
    const groupedEvent = groupBy(formattedEvents, e => !isUndefined(e.timestamp) ? getYear(e.timestamp) : undefined)
    const dates = keys(groupedEvent).map(d => parseInt(d)).filter(d => !!d)
    return (
        <Grid container direction='column' spacing={1}>
            {
                orderBy(dates, d => d, 'desc').map((date, i) => (
                    <GroupAccordion
                        key={date}
                        title={date}
                        events={groupedEvent[date]}
                        defaultExpanded={i === 0}
                        headers={['eventType', 'station', 'date', 'cause', 'diagnostics', 'actions']}
                    />
                ))
            }
        </Grid>
    )
}

EventsGroupByDate.propTypes = {
    events: PropTypes.arrayOf(PropTypes.instanceOf(DtoInterventionEvent)),
}

const EventsGroupByReferent = ({
    events = [],
}) => {
    const {
        eventDiagnostics,
        eventSolutions,
        piezometers,
        contacts,
    } = useSelector(store => ({
        eventDiagnostics: store.EventsReducer.eventPiezometerDiagnostics,
        eventSolutions: store.EventsReducer.eventPiezometerSolutions,
        piezometers: store.PiezometryReducer.piezometersLight,
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const piezometersIndexed = useListIndexed(piezometers)
    const contactsIndexed = useListIndexed(contacts)

    const interventionPreventive = useSandreList('CAUSE_INTERVENTION.PREVENTIF')
    const interventionCurative = useSandreList('CAUSE_INTERVENTION.CURATIF')
    const interventionOther = useSandreList('CAUSE_INTERVENTION.AUTRE')

    const interventions = {
        PREVENTIF: interventionPreventive,
        CURATIF: interventionCurative,
        AUTRE: interventionOther,
    }

    const eventTypeInfo = useMemo(() => getEventTypeInfo(), [])

    const formattedEvents = events.map(e => {
        const causes = e.interventions?.map(intervention => {
            const [type, id] = intervention.split(':')
            const parseId = parseInt(id)
            return interventions[type]?.find(i => i.code === parseId)?.name ?? ''
        })

        const diagnostics = e.uniqDiagnostics.map(id => eventDiagnostics.find(d => d.codeDiagnostic === id)?.description).filter(d => !!d)
        const solutions = e.uniqSolutions.map(id => eventSolutions.find(d => d.codeSolution === id)?.description).filter(d => !!d)

        const piezo = piezometersIndexed[e.piezometer]
        return {
            eventType: {
                value: (
                    <Grid container spacing={1}>
                        <Icon color='black'>{eventTypeInfo[e.eventType].icon}</Icon>
                        {eventTypeInfo[e.eventType].label}
                    </Grid>
                ),
                sortValue: eventTypeInfo[e.eventType].label,
            },
            date: { value: getDate(e.date), sortValue: e.date },
            station: {
                value: (
                    <Tooltip title={piezo?.name}>
                        {piezo?.code}
                    </Tooltip>
                ),
                sortValue: piezo?.code,
            },
            cause: { value: causes?.join(', ') ?? '' },
            diagnostics: {
                value: e.uniqDiagnostics.length ? (
                    <Tooltip title={(<TooltipList listLabel={diagnostics} />)}>
                        {e.uniqDiagnostics.length}
                    </Tooltip>
                ) : 0,
                sortValue: e.uniqDiagnostics.length,
            },
            actions: { value: e.uniqSolutions.length ? (
                <Tooltip title={(<TooltipList listLabel={solutions} />)}>
                    {e.uniqSolutions.length}
                </Tooltip>
            ) : 0,
            sortValue: e.uniqSolutions.length,
            },
            referent: contactsIndexed[e.referent]?.name ?? i18n.unknown,

            timestamp: e.date,
            stationCode: piezo?.code,
            interventions: e.interventions,
        }
    })
    const groupedEvents = groupBy(formattedEvents, 'referent')
    const orderedKeys = uniq([i18n.unknown, ...keys(groupedEvents)]).reverse()
    return (
        <Grid container direction='column' spacing={1}>
            {
                orderedKeys.map((key, i) => (
                    <GroupAccordion
                        key={key}
                        title={key}
                        events={groupedEvents[key]}
                        defaultExpanded={i === 0}
                        headers={['eventType', 'station', 'date', 'cause', 'diagnostics', 'actions']}
                    />
                ))
            }
        </Grid>
    )
}

EventsGroupByReferent.propTypes = {
    events: PropTypes.arrayOf(PropTypes.instanceOf(DtoInterventionEvent)),
}

const EventsGroupByEventType = ({
    events = [],
}) => {
    const {
        eventDiagnostics,
        eventSolutions,
        piezometers,
    } = useSelector(store => ({
        eventDiagnostics: store.EventsReducer.eventPiezometerDiagnostics,
        eventSolutions: store.EventsReducer.eventPiezometerSolutions,
        piezometers: store.PiezometryReducer.piezometersLight,
    }), shallowEqual)

    const piezometersIndexed = useListIndexed(piezometers)

    const interventionPreventive = useSandreList('CAUSE_INTERVENTION.PREVENTIF')
    const interventionCurative = useSandreList('CAUSE_INTERVENTION.CURATIF')
    const interventionOther = useSandreList('CAUSE_INTERVENTION.AUTRE')

    const interventions = {
        PREVENTIF: interventionPreventive,
        CURATIF: interventionCurative,
        AUTRE: interventionOther,
    }

    const eventTypeInfo = getEventTypeInfo()

    const formattedEvents = events.map(e => {
        const causes = e.interventions?.map(intervention => {
            const [type, id] = intervention.split(':')
            const parseId = parseInt(id)
            return interventions[type]?.find(i => i.code === parseId)?.name ?? ''
        })

        const diagnostics = e.uniqDiagnostics.map(id => eventDiagnostics.find(d => d.codeDiagnostic === id)?.description).filter(d => !!d)
        const solutions = e.uniqSolutions.map(id => eventSolutions.find(d => d.codeSolution === id)?.description).filter(d => !!d)

        const piezo = piezometersIndexed[e.piezometer]

        return {
            date: { value: getDate(e.date), sortValue: e.date },
            station: { value: piezo?.code },
            stationName: { value: piezo?.name },
            cause: { value: causes?.join(', ') ?? '' },
            diagnostics: {
                value: e.uniqDiagnostics.length ? (
                    <Tooltip title={(<TooltipList listLabel={diagnostics} />)}>
                        {e.uniqDiagnostics.length}
                    </Tooltip>
                ) : 0,
                sortValue: e.uniqDiagnostics.length,
            },
            actions: { value: e.uniqSolutions.length ? (
                <Tooltip title={(<TooltipList listLabel={solutions} />)}>
                    {e.uniqSolutions.length}
                </Tooltip>
            ) : 0,
            sortValue: e.uniqSolutions.length,
            },
            eventType: e.eventType,

            timestamp: e.date,
            stationCode: piezo?.code,
            interventions: e.interventions,
        }
    })

    const groupedEvents = groupBy(formattedEvents, 'eventType')

    return (
        <Grid container direction='column' spacing={1}>
            {
                keys(groupedEvents).map((key, i) => !!groupedEvents[key]?.length && (
                    <GroupAccordion
                        key={key}
                        title={eventTypeInfo[key]?.label}
                        events={groupedEvents[key]}
                        defaultExpanded={i === 0}
                        headers={['station', 'stationName', 'date', 'cause', 'diagnostics', 'actions']}
                    />
                ))
            }
        </Grid>
    )
}

EventsGroupByEventType.propTypes = {
    events: PropTypes.arrayOf(PropTypes.instanceOf(DtoInterventionEvent)),
}

const EventsGroupByStation = ({
    events = [],
}) => {
    const {
        eventDiagnostics,
        eventSolutions,
        piezometers,
    } = useSelector(store => ({
        eventDiagnostics: store.EventsReducer.eventPiezometerDiagnostics,
        eventSolutions: store.EventsReducer.eventPiezometerSolutions,
        piezometers: store.PiezometryReducer.piezometersLight,
    }), shallowEqual)

    const piezometersIndexed = useListIndexed(piezometers)

    const interventionPreventive = useSandreList('CAUSE_INTERVENTION.PREVENTIF')
    const interventionCurative = useSandreList('CAUSE_INTERVENTION.CURATIF')
    const interventionOther = useSandreList('CAUSE_INTERVENTION.AUTRE')

    const interventions = {
        PREVENTIF: interventionPreventive,
        CURATIF: interventionCurative,
        AUTRE: interventionOther,
    }

    const eventTypeInfo = useMemo(() => getEventTypeInfo(), [])

    const formattedEvents = events.map(e => {
        const causes = e.interventions?.map(intervention => {
            const [type, id] = intervention.split(':')
            const parseId = parseInt(id)
            return interventions[type]?.find(i => i.code === parseId)?.name ?? ''
        })

        const diagnostics = e.uniqDiagnostics.map(id => eventDiagnostics.find(d => d.codeDiagnostic === id)?.description).filter(d => !!d)
        const solutions = e.uniqSolutions.map(id => eventSolutions.find(d => d.codeSolution === id)?.description).filter(d => !!d)

        const piezo = piezometersIndexed[e.piezometer]
        return {
            eventType: {
                value: (
                    <Grid container spacing={1}>
                        <Icon color='black'>{eventTypeInfo[e.eventType].icon}</Icon>
                        {eventTypeInfo[e.eventType].label}
                    </Grid>
                ),
                sortValue: eventTypeInfo[e.eventType].label,
            },
            date: { value: getDate(e.date), sortValue: e.date },
            cause: { value: causes?.join(', ') ?? '' },
            diagnostics: {
                value: e.uniqDiagnostics.length ? (
                    <Tooltip title={(<TooltipList listLabel={diagnostics} />)}>
                        {e.uniqDiagnostics.length}
                    </Tooltip>
                ) : 0,
                sortValue: e.uniqDiagnostics.length,
            },
            actions: { value: e.uniqSolutions.length ? (
                <Tooltip title={(<TooltipList listLabel={solutions} />)}>
                    {e.uniqSolutions.length}
                </Tooltip>
            ) : 0,
            sortValue: e.uniqSolutions.length,
            },
            stationId: e.piezometer,

            timestamp: e.date,
            stationCode: piezo?.code,
            interventionType: e.interventionType,
        }
    })
    const groupedEvents = groupBy(formattedEvents, 'stationId')
    const orderedKeys = uniq(['undefined', ...keys(groupedEvents)]).reverse()
    return (
        <Grid container direction='column' spacing={1}>
            {
                orderedKeys.map((key, i) => {
                    const piezo = piezometersIndexed[key]
                    return (
                        <GroupAccordion
                            key={key}
                            title={`${piezo?.code ? `[${piezo.code}] `: ''}${piezo?.name ?? ''}` || i18n.unknown}
                            events={groupedEvents[key]}
                            defaultExpanded={i === 0}
                            headers={['eventType', 'date', 'cause', 'diagnostics', 'actions']}
                        />
                    )
                })
            }
        </Grid>
    )
}

EventsGroupByStation.propTypes = {
    events: PropTypes.arrayOf(PropTypes.instanceOf(DtoInterventionEvent)),
}

const EventsGroupByCauseIntervention = ({
    events = [],
}) => {
    const {
        eventDiagnostics,
        eventSolutions,
        piezometers,
    } = useSelector(store => ({
        eventDiagnostics: store.EventsReducer.eventPiezometerDiagnostics,
        eventSolutions: store.EventsReducer.eventPiezometerSolutions,
        piezometers: store.PiezometryReducer.piezometersLight,
    }), shallowEqual)

    const piezometersIndexed = useListIndexed(piezometers)

    const interventionPreventive = useSandreList('CAUSE_INTERVENTION.PREVENTIF')
    const interventionCurative = useSandreList('CAUSE_INTERVENTION.CURATIF')
    const interventionOther = useSandreList('CAUSE_INTERVENTION.AUTRE')

    const interventions = {
        PREVENTIF: interventionPreventive,
        CURATIF: interventionCurative,
        AUTRE: interventionOther,
    }

    const eventTypeInfo = getEventTypeInfo()

    const formattedEvents = events.flatMap(e => {
        const causes = e.interventions?.map(intervention => {
            const [type, id] = intervention.split(':')
            const parseId = parseInt(id)
            return interventions[type]?.find(i => i.code === parseId)?.name ?? ''
        })

        const diagnostics = e.uniqDiagnostics.map(id => eventDiagnostics.find(d => d.codeDiagnostic === id)?.description).filter(d => !!d)
        const solutions = e.uniqSolutions.map(id => eventSolutions.find(d => d.codeSolution === id)?.description).filter(d => !!d)

        const piezo = piezometersIndexed[e.piezometer]
        const baseEvent = {
            eventType: {
                value: (
                    <Grid container spacing={1}>
                        <Icon color='black'>{eventTypeInfo[e.eventType].icon}</Icon>
                        {eventTypeInfo[e.eventType].label}
                    </Grid>
                ),
                sortValue: eventTypeInfo[e.eventType].label,
            },
            date: { value: getDate(e.date), sortValue: e.date },
            station: {
                value: (
                    <Tooltip title={piezo?.name}>
                        {piezo?.code}
                    </Tooltip>
                ),
                sortValue: piezo?.code,
            },
            cause: { value: causes?.join(', ') ?? '' },
            diagnostics: {
                value: e.uniqDiagnostics.length ? (
                    <Tooltip title={(<TooltipList listLabel={diagnostics} />)}>
                        {e.uniqDiagnostics.length}
                    </Tooltip>
                ) : 0,
                sortValue: e.uniqDiagnostics.length,
            },
            actions: { value: e.uniqSolutions.length ? (
                <Tooltip title={(<TooltipList listLabel={solutions} />)}>
                    {e.uniqSolutions.length}
                </Tooltip>
            ) : 0,
            sortValue: e.uniqSolutions.length,
            },
            timestamp: e.date,
            stationCode: piezo?.code,
            interventions: e.interventions,
        }
        if (!causes?.length) {
            return [{ ...baseEvent, intervention: i18n.unknown }]
        }
        return causes.map(c => ({
            ...baseEvent,
            intervention: c,
        }))
    })

    const groupedEvents = groupBy(formattedEvents, 'intervention')
    const orderedKeys = uniq([i18n.unknown, ...keys(groupedEvents)]).reverse()
    return (
        <Grid container direction='column' spacing={1}>
            {
                orderedKeys.map((key, i) => (
                    <GroupAccordion
                        key={key}
                        title={key}
                        events={groupedEvents[key]}
                        defaultExpanded={i === 0}
                        headers={['eventType', 'station', 'date', 'cause', 'diagnostics', 'actions']}
                    />
                ))
            }
        </Grid>
    )
}

EventsGroupByCauseIntervention.propTypes = {
    events: PropTypes.arrayOf(PropTypes.instanceOf(DtoInterventionEvent)),
}

export {
    EventsGroupByDate,
    EventsGroupByReferent,
    EventsGroupByEventType,
    EventsGroupByStation,
    EventsGroupByCauseIntervention,
}